<template>
  <div>
    <button
      type="button"
      @click="toggleLayer('base')"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey; margin-bottom: 11px;"
    >
    {{ i18nValues["standard"] }} 
    </button>
    <button
      type="button"
      @click="toggleLayer('satellite')"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey;"
    >
    {{ i18nValues["satellite"] }} 
    </button>
    <button
      type="button"
      @click="toggleLayer('topography')"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey;"
    >
    {{ i18nValues["relief"] }} 
    </button>
    <div
      id="map"
      style="width: 100% !important; height: 500px !important; margin-bottom: 25px;"
    ></div>
    <div ref="tooltip" class="ol-tooltip"></div>
  </div>
</template>

<script>
import { Feature, Map, View, Overlay } from "ol/index";
import { OSM, Vector as VectorSource, XYZ } from "ol/source";
import { Point } from "ol/geom";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { useGeographic } from "ol/proj";
import { Style, Icon } from "ol/style";
import { defaults as defaultInteractions, MouseWheelZoom, DoubleClickZoom, PinchZoom } from "ol/interaction";
import VueCookie from 'vue-cookie';

export default {
  name: "MapDashboard",
  props: {
    fields: Array,
  },
  created() {
    useGeographic();
  },
  mounted() {
    this.place = [-65.258006, -35.138403];
    // Define the base OSM layer
    this.baseLayer = new TileLayer({
      source: new OSM({
        maxZoom: 19, // OSM typically supports up to zoom 19
      }),
    });

    // Initialize your vector layer for markers
    this.vector = new VectorLayer({
      source: new VectorSource(),
    });

    // Define the satellite layer
    this.satelliteLayer = new TileLayer({
      source: new XYZ({
        url:
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        maxZoom: 19, // ArcGIS World Imagery supports up to zoom 19
        visible: false, // Start with this layer hidden
      }),
    });

    // Define the topography layer
    this.topographyLayer = new TileLayer({
      source: new XYZ({
        url: "https://a.tile.opentopomap.org/{z}/{x}/{y}.png",
        maxZoom: 17, // OpenTopoMap typically supports up to zoom 17
        visible: false, // Start with this layer hidden
      }),
    });

    // Initialize the map with all layers
    // Use a conservative maxZoom (17) to ensure tiles are always available
    const MAX_ZOOM = 17;
    const view = new View({
      center: this.place,
      zoom: 6,
      maxZoom: MAX_ZOOM, // Prevent zooming beyond what tile sources support
      constrainResolution: true, // Force zoom to integer levels
    });
    
    // Explicitly set maxZoom using the setter
    view.setMaxZoom(MAX_ZOOM);
    
    // Store view reference for later use
    this.view = view;
    
    // Override setZoom to always respect maxZoom - this catches ALL zoom operations
    const originalSetZoom = view.setZoom.bind(view);
    view.setZoom = function(zoom) {
      if (zoom !== null && zoom !== undefined) {
        const safeZoom = Math.min(zoom, MAX_ZOOM);
        return originalSetZoom(safeZoom);
      }
      return originalSetZoom(zoom);
    };
    
    // Override animate to respect maxZoom
    const originalAnimate = view.animate.bind(view);
    view.animate = function(opt_animationOptions) {
      if (opt_animationOptions && opt_animationOptions.zoom !== undefined) {
        opt_animationOptions.zoom = Math.min(opt_animationOptions.zoom, MAX_ZOOM);
      }
      return originalAnimate(opt_animationOptions);
    };
    
    // Override fit to always respect maxZoom
    const originalFit = view.fit.bind(view);
    view.fit = function(geometryOrExtent, opt_options) {
      const options = opt_options || {};
      if (options.maxZoom === undefined || options.maxZoom > MAX_ZOOM) {
        options.maxZoom = MAX_ZOOM;
      }
      return originalFit(geometryOrExtent, options);
    };
    
    // Add aggressive constraint listeners to catch any zoom that slips through
    const enforceMaxZoom = () => {
      const currentZoom = view.getZoom();
      if (currentZoom !== null && currentZoom !== undefined && currentZoom > MAX_ZOOM) {
        // Use requestAnimationFrame to ensure this runs after any zoom operation
        requestAnimationFrame(() => {
          const checkZoom = view.getZoom();
          if (checkZoom !== null && checkZoom !== undefined && checkZoom > MAX_ZOOM) {
            originalSetZoom(MAX_ZOOM);
          }
        });
        originalSetZoom(MAX_ZOOM);
      }
    };
    
    // Listen to all possible zoom-related events with immediate enforcement
    view.on('change:resolution', enforceMaxZoom);
    view.on('change:zoom', enforceMaxZoom);
    view.on('moveend', enforceMaxZoom);
    
    // Also add a more frequent check using requestAnimationFrame
    // Store the animation frame ID so we can cancel it if needed
    let continuousCheckFrameId = null;
    const continuousCheck = () => {
      const currentZoom = view.getZoom();
      if (currentZoom !== null && currentZoom !== undefined) {
        if (currentZoom > MAX_ZOOM) {
          originalSetZoom(MAX_ZOOM);
        }
      }
      continuousCheckFrameId = requestAnimationFrame(continuousCheck);
    };
    continuousCheckFrameId = requestAnimationFrame(continuousCheck);
    
    // Store references for cleanup
    this.continuousCheckFrameId = continuousCheckFrameId;
    this.MAX_ZOOM = MAX_ZOOM;
    this.originalSetZoom = originalSetZoom;
    this.enforceMaxZoom = enforceMaxZoom;
    
    // Configure zoom interactions to respect maxZoom
    const mouseWheelZoom = new MouseWheelZoom({
      constrainResolution: true,
    });
    
    const doubleClickZoom = new DoubleClickZoom({
      duration: 250,
    });
    
    const pinchZoom = new PinchZoom({
      constrainResolution: true,
    });
    
    this.map = new Map({
      target: "map",
      view: view,
      layers: [
        this.satelliteLayer,
        this.topographyLayer,
        this.baseLayer,
        this.vector,
      ],
      interactions: defaultInteractions({
        mouseWheelZoom: mouseWheelZoom,
        doubleClickZoom: doubleClickZoom,
        pinchZoom: pinchZoom,
      }),
    });
    
    // Also check after any map interaction
    this.map.on('moveend', enforceMaxZoom);

    this.tooltip = this.$refs.tooltip;

    this.tooltipOverlay = new Overlay({
      element: this.tooltip,
      positioning: "center-center",
    });
    this.map.addOverlay(this.tooltipOverlay);

    this.map.on("pointermove", (event) => {
      this.tooltipOverlay.setPosition(undefined);
      this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        var name = feature.get("name");
        if (name) {
          var coordinate = event.coordinate;
          this.tooltip.innerHTML = name;
          this.tooltipOverlay.setPosition(coordinate);
        }
      });
    });

    this.markFields(this.fields);
    this.$nextTick(() => {
      this.fitToFields();
    });

    this.map.on("click", (event) => {
      this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        if (feature) {
          const coordinates = feature.getGeometry().getCoordinates();
          this.map.getView().animate({
            center: coordinates,
            zoom: Math.min(9, 17), // Respect maxZoom
            duration: 1000,
          });
        }
      });
    });

    const savedLayer = VueCookie.get('preferred_layer');
    if (savedLayer) {
      this.toggleLayer(savedLayer);
    } else {
      this.toggleLayer('base');
    }

  },
  data() {
    return {
      locations: [],
      tooltip: null,
      tooltipOverlay: null,
      continuousCheckFrameId: null,
      MAX_ZOOM: 17,
      originalSetZoom: null,
      enforceMaxZoom: null,
    };
  },
  beforeDestroy() {
    // Clean up the continuous check animation frame
    if (this.continuousCheckFrameId !== null) {
      cancelAnimationFrame(this.continuousCheckFrameId);
      this.continuousCheckFrameId = null;
    }
  },
  methods: {
    toggleLayer(layerType) {
      // Check if layers are initialized before trying to use them
      if (!this.baseLayer || !this.satelliteLayer || !this.topographyLayer) {
        // Layers not initialized yet, return early
        return;
      }

      // Hide all layers initially
      this.baseLayer.setVisible(false);
      this.satelliteLayer.setVisible(false);
      this.topographyLayer.setVisible(false);

      // Based on the layer type, make the respective layer visible
      switch (layerType) {
        case "base":
          this.baseLayer.setVisible(true);
          break;
        case "satellite":
          this.satelliteLayer.setVisible(true);
          break;
        case "topography":
          this.topographyLayer.setVisible(true);
          break;
      }

      VueCookie.set('preferred_layer', layerType)
    },

    markFields(fields) {
      fields.forEach((field) => {
        // if (!field.location.match(/[a-z]/i)) {
        //   this.addMarker(field);
        // }
        if (field.location && typeof field.location === 'string' && !field.location.match(/[a-z]/i)) {
          this.addMarker(field);
        }
      });
    },
    fitToFields() {
      if (!this.map || !this.vector) {
        return;
      }

      const source = this.vector.getSource();
      const features = source.getFeatures();

      if (!features.length) {
        return;
      }

      const extent = source.getExtent();
      if (!extent || extent.length !== 4) {
        return;
      }

      // Check if extent is valid (not empty)
      const isExtentEmpty = extent[0] === extent[2] && extent[1] === extent[3];
      if (isExtentEmpty) {
        // If extent is empty (single point), center on it with a reasonable zoom
        const center = [extent[0], extent[1]];
        this.map.getView().animate({
          center: center,
          zoom: Math.min(12, 17), // Respect maxZoom
          duration: 500,
        });
        return;
      }

      // Fit to extent with padding
      this.map.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 17,
        duration: 500,
      });
    },
    addMarker(field) {
      var marker = new Feature(
        new Point(JSON.parse("[" + field.location + "]").reverse())
      );
      marker.set("name", field.name);
      var zIndex = 1;
      marker.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 36],
            anchorXUnits: "fraction",
            anchorYUnits: "pixels",
            opacity: 1,
            src:
                "data:image/png;base64,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",
            // src:
            //   "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGUAAAB/CAYAAAD2MQwyAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAArdSURBVHhe7Z3bbxxXGcDPOTOzV192fb80dtykS4tCGjVVRaloi9qCBIIX/oOKlyIeeEVCPCDxBBJPiEpUvKA+8BAhhEoFpbSmt5SGorZOXNt1bcdeO/ba2Zu9O9czfN/MbOMQO90kM/XE/n6rs7MzZ5yZOb/5zmV2ZsMIgiAIgiAIgiAIgiAIgiAIgiAIgiAIgiCOKjyY3hHZbHZ0aGjoW8lkshdmueu6rNlsMimll68oipPL5Vamp6df1nW95i0k9iUUKb29vQ+eOXPmV11dXQ+AAI4yKpUKMwyDoaB0Om1NTEz859y5cz8ul8vLwZ8R+yCC6R0hhFBTqVQfRMxwOpsdSXZkRlJZSDBNd2ZHIG8kn8/f8+CZB7+iqmoi+DNiH0KJlP7+/rOPPPLIC7l8/rRQFZGwTDa8UmJac8fbAkSPHB+/d71s6698OHXpfL1eh4yDw/VfbsWyK+9Wym/XDH0ryIoFoUrJ53wpOWOHfX9qiQ3Wa9DAcMY5Z1oqJU1VKztCNl0JddoBIuHlgJsF3Zz7aan4k4u1yntBViwIV0o+50uBCHn2g0/Y8HaVKY4AKZhgU7g1Fxr/ULZ6+0jYB5Ai501z+kfN+nPvb9feCLJiQShtSgs8/SV3mXBBAiThaoEACXlQDN4U1oNAOcjEYS+EhD3x4vVAg3ZPQpUCFZUnBGosJjwZMO9FCSTcFFZl/loHmGDHYD8kRC6eJgcetnsQqhQiHEhKDDmSUrwqLIZtSYsjJwWbea9t+6wpOeQN/d2CDT1EB+SorqL0d+dzMLhVg6xYEErX49o4xR885psN9gMYpwzBwF2BbnDsgG6xAaOVq7Zce/mhU3/6y8aVPy8Wi8VwSqN9TMvWN6uVomXbRrDI42hKgTEKdIqZoShy4fTp4trwYMlymYlZUVYdu0vC5Y67apoLL0y+9utLl5cuBIs9jqQUHEDCaMobpyyqaVnnqrRhuOvnRofjSsYFDqpdZrvcmD85/Mbzc1M/n1pePh+s4nEEG3pfij+yh3bFcYRwHFVzHC2qBNvQFAmfXVdLwGchpVLR3PUpq/lm2bLW/D27xpFs6L2u17XuF1QlnLkiuoTb8k4ASFBzuo7kdm18dPHCyuW3r5RKxWA3PuNoSgFQCV4k9RKET6TJ26ILQnCGs0pGLX7A7dfWmo1Fx3FsL3sXR1YK0qrGoga24m3HgXcJEqrH71k4v/jp5F5RghxpKV8cfqxw6F1UMonlj5g1uVavXYYosbyM/4OkfBFAlNgQKtiWVO8bX3h74ZPXS1tbNzTwLUhKBGCFuLtShGaEKYpwN9PayofcmVyt15bsfaIEISkR4UnBNgte0JJDlLhO/b6xhTfmZl7duEmUICQlCkAGhySDLjHnUkJbsvIxk2+ulsvLtm17Vw/2g6REQOt+BIwS7kCPy9WcamF8/l9zM/+4Wq1sBKvtC0mJgFYX2BujYJR0JYvTqnhrpXJ1ybKs6y4+7gVJiQwYl8Bo0ZZCVk4cn39t+uLfytVqKci8KSQlSjQp653p1VnNPb9c2lgyTVMPcm4KSYkIx4WmXmp25UvH59+EHldte/tqkPW5kJQIwPG7qzKn2t2x9nFGe2duZXm23ShBSEoYQKOOX2E50LJj4+6NUVRhV06OzU5enPr7dqNRwUXtQlJCAG8+xK/yUIg3r7hOtat7bTatnJ+5vDSj63rDz2kPkhIKMEiEJECKREGKZpXHRmfemp1+tdFs3vJDUiQlBFAJFqQnRHC73tGxPt+ZvDA1M3vRMIymv1b7kJQQ8GstjBV4dxVZziRKi+XNGdOy2m7cd0NSQgLFYKSorql0VLd7+oR2jyLEbd1PRlJCwGvgobX3Ji5XeraN0fst57Hx4ZGJ27nRj6SEAN54gdcgsaF3FZRjqv1La4XH73/g6Uw63Rms1jYkJSS83hcm6Bvj7bA9TXPslMGeHBsamhBCKMFqbUFSQgAb+OsLEuXYWt9SsfBE4YFnOrPZXJDRFiQlAlCSKoXS27THTjWdJ8YHBm8pWkhKRGCjL4St5i+vFh47UXiqM9vRdrREIAV3B9/hBd2S1hc+/nKca32KMuE7frh+25j8sbc/hx+8bwmjAP5d1YFoMeSxUyZ7crSnZ6zdaLmlBmg/stnsyOjo6PdS6dQgVzjPWA47vb7FusymdzMzPqZtQzfelq5hMm7aNrNsGU1yYFOOtCR0RRUO/VNfBLCr7PEWUnwQFT9tQzkZeEPW7hXCAK0LPOddLut1zgoT5fnN0mxT1z/3hx1C2ZPWXfe5fO40V4Xo0XX23YuLbGSnzjiUkisE1LBaY9nSL65VqkUpHf/B3AhIqGpiqKNzZEIVX3ZNK6NKF4o8eCoY8OLFu5qLyxgrwfiuxlVYFn6l4X0lDPItlxkzJ4b++duFmZ9Nryz/V0IBBKvsSchS/Ech4FxlqY0rTOj4aA5niUTSGj9x4tJHCwvPv/Lvd1+qNXYi+yWjrkym85tnz377aS39nDY1c0oYthoM6/wVANeFMoHCysC4Lq2kmCK00KuxlhAE753YUNmnr08M/u6P77/3+0q1uull7EOo1Vc6nR5UhMAbOFjZMlkZztBaQrCqKpzkYN/8S5OTv1nb2lywHEe3pDSiSNuGUdvc3q6cPXbvM05x7ZglHcWAsYMBJ4cJCacWFJbgGkslkkwDMVh4rUgKC/w3W2IEvKUc3sG7Umy6uXNhq15fD1bbkwgaejg8OCkd+KcdqFMdqCYkPjrEVVc3TQN2NPKniBq6rpvcdfDarZT+jxg4ECmYsOrSBGedCY1loNryK7FwhbRoRYrfvLhq78rWya+fLDyV7871exn7ELqUGw8vmgNui2DTGAXY5MOpy5JwyDktxdJQZcEZ7K+wq2qLCqwwe23nGIzyvzGY7RgLFu9JBJESP7CLjHcspqCnlYMqKwNCVE8IpOubm0jAmiMBu6Bxpg2srt/36PjEE/lc977RcuileOUNZZ/mgRBVDYT4VRrmtaqZyEArsC0bxAzYbOyMLZ4aSGeP+Zk3cmil4IFhWWBxJ+CtK5GAnhZUWX6O9/IPPmIhCOwHBCrT8HKy4Ine9c2TXx07/nhvLjcQrHEdh1aKhCNrNep5aEM61ATToEx8BXDKwrt3VRem+DnS5LngDMbVMM/ZgMWPnbH508OZjgnIvoFDKwXKXaa4kN1aQmYUDfpgXNrMlQ6HhFcXoBf4hSYO24TtQofDFQpPDpSqhYeGR76W7+7uC/b4M1DkHbP7OXpVVYVt296vrVqW5fXVFUUxC4XCOy+++OKzm5ubnwZ/FhmdmUzfDx9+9BfDc4sPp0ypqfiErneoWLeHcsh3AOd9TIzCGFuc706e+8PGyi+XylsfB5keh1IK/vrraL6nkLHsnMCB0kF72AXsCk8KJQNVmVZ15ZWivjPXMM1qkO1xKKXc7RzeNuUuhqTEEJISQ0hKDCEpMYSkxBCSEkNISgwhKTGEpMQQkhJDSEoMISkxhKTEEJISQ0hKDCEpMYSkxBCSEkNISgwhKTGEpMQQkhJDSEoMISkxhKTEEJISQ0hKDCEpMYSkxBCSEkNISgwhKTGEpMQQkhJDQpeCzzgSd0YoUhzHMYGSruurOzs7q41Gw0vNZtObNwxjDaZXP+/HxwifUJ4OTqVSvSMjI9+B6SAHoPAZCGI4xcjBRf39/VcuXbr0V5DV9v+4QxAEQRAEQRAEQRAEcbfA2P8AFvCBJo1V9I0AAAAASUVORK5CYII=",
            zIndex: zIndex,
          }),
          zIndex: zIndex,
        })
      );
      this.vector.getSource().addFeature(marker);
    },
  },
  watch: {
    fields: {
      handler() {
        this.vector.getSource().clear();
        this.markFields(this.fields);
        this.$nextTick(() => {
          this.fitToFields();
        });
      },
      deep: true,
    },
  },
};
</script>

<style>
.ol-tooltip {
  position: absolute;
  background-color: #000;
  color: #fff;
  border-radius: 5px;
  padding: 6px 12px;
  white-space: nowrap;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
  opacity: 0.8;
}
</style>
